// Conseiller de cohérence — Règle B : budget clairement trop bas pour le groupe/saison.
// Heuristique de PLANCHER conservateur : on ne prévient QUE si le budget est nettement
// sous le minimum plausible (croisière + vol + hôtel + taxes). Doux, jamais bloquant ;
// le filet post-recherche (2e couche) rattrape les cas borderline qu'on laisse passer.
//
// ⚠️ CONSTANTES TUNABLES — calibrer si ça crie au loup trop/pas assez. Tout est en CAD.

const CRUISE_FLOOR_ADULT = 1200; // /adulte, intérieure, grand public, 7 nuits, hors-pointe
const PEAK_MULT          = 1.6;  // multiplicateur croisière en période de pointe
const KID_FACTOR         = 0.5;  // enfant 2-12 ≈ moitié du tarif adulte (plancher)
const FLIGHT_SEAT        = 500;  // /siège A-R depuis le Canada, hors-pointe
const FLIGHT_PEAK_MULT   = 1.4;  // vols plus chers en pointe
const HOTEL_FLOOR        = 200;  // 1 nuit veille
const TAXES_PP           = 150;  // taxes/frais portuaires par personne
const TRIGGER_RATIO      = 0.7;  // avis SEULEMENT si budget < 70 % du plancher (conservateur)

// Mois de pointe : décembre (fêtes), janv-mars (haute saison Caraïbes), juillet-août (été).
export function isPeakPeriod(data) {
  let m = data?.dates?.month;
  if (m == null && data?.dates?.from) m = parseInt(String(data.dates.from).slice(5, 7), 10);
  return [12, 1, 2, 3, 7, 8].includes(m);
}

// Plancher CONSERVATEUR en CAD. `party` = { adults, children, flightSeats } (calculés côté App).
export function budgetFloor(data, party) {
  const adults   = Math.max(1, party?.adults || 1);
  const children = party?.children || 0;            // 2-12 ans (bébés ~0 croisière)
  const seats    = party?.flightSeats != null ? party.flightSeats : adults;
  const peak     = isPeakPeriod(data);

  const cruiseAdult = CRUISE_FLOOR_ADULT * (peak ? PEAK_MULT : 1);
  const cruise = cruiseAdult * adults + cruiseAdult * KID_FACTOR * children;
  const flight = (data?.departure === "drive_ny")
    ? 0
    : FLIGHT_SEAT * (peak ? FLIGHT_PEAK_MULT : 1) * seats;
  const taxes  = TAXES_PP * (adults + children);
  return Math.round(cruise + flight + HOTEL_FLOOR + taxes);
}

// Retourne null si OK, sinon un avis bilingue (DOUX, multi-leviers, dates en DERNIER).
// `party` = { adults, children, flightSeats }.
export function assessBudget(data, party) {
  const budget = data?.budget;
  if (!budget || budget <= 0) return null;          // pas de budget → rien à juger
  const floor = budgetFloor(data, party);
  if (budget >= floor * TRIGGER_RATIO) return null; // au-dessus du seuil → pas d'avis

  const peak    = isPeakPeriod(data);
  const target  = Math.ceil(floor / 500) * 500;     // cible réaliste, arrondie
  const isInside = (data?.cabin || []).length === 1 && data.cabin[0] === "inside";
  const totalPax = (party?.adults || 1) + (party?.children || 0);
  const fmt = n => Number(n).toLocaleString("fr-CA");
  const fmtEn = n => Number(n).toLocaleString("en-CA");

  // Leviers ORDONNÉS — budget/cabine/compagnie d'abord, dates en DERNIER (souvent fixes).
  const leversFr = [`Prévoir un budget plus proche de ~${fmt(target)} $`];
  const leversEn = [`Plan for a budget closer to ~$${fmtEn(target)}`];
  if (!isInside) { leversFr.push("Viser une cabine intérieure (la moins chère)"); leversEn.push("Aim for an interior cabin (the cheapest)"); }
  leversFr.push("Choisir une compagnie grand public plus abordable");
  leversEn.push("Pick a more affordable mainstream cruise line");
  if (peak) { leversFr.push("En dernier recours : décaler hors des périodes de pointe"); leversEn.push("As a last resort: shift outside peak periods"); }

  const groupFr = `${totalPax} voyageur${totalPax > 1 ? "s" : ""}`;
  const groupEn = `${totalPax} traveller${totalPax > 1 ? "s" : ""}`;
  const whenFr = peak ? " en période de pointe" : "";
  const whenEn = peak ? " in peak season" : "";

  return {
    reason: "budget_low",
    editStep: "budget",   // bouton « Ajuster » → ramène l'user à l'étape budget
    title_fr: "Cette recherche risque d'être difficile",
    title_en: "This search may be tough",
    body_fr: `Pour ${groupFr}${whenFr}, ${fmt(budget)} $ est très serré — on risque de trouver peu, voire rien. Pour mettre les chances de votre côté, un compromis aiderait :`,
    body_en: `For ${groupEn}${whenEn}, $${fmtEn(budget)} is very tight — we may find little, or nothing. To improve your odds, a compromise would help:`,
    levers_fr: leversFr,
    levers_en: leversEn,
  };
}
